import { Button, Empty, Modal, Timeline } from 'antd';
import { useEffect, useRef, useState } from 'react';
import Loading from '../loading';
import NotificationAlert from '../../components/notification/index';
import { AnnualReviewAuditTrail } from '../../types/AnnualReview';
import { getAnnualReviewAuditTrail } from '../../services/annualServices';

interface Props {
  open: boolean;
  setOpen(open: boolean): void;
  id: string;
}

enum OperationType {
  Opened = 'ANNUAL_REVIEW_OPENED',
  Saved = 'ANNUAL_REVIEW_SAVED',
  Submitted = 'ANNUAL_REVIEW_SUBMITTED',
}
const OperationTypeLabel: { [key: string]: string } = {
  [OperationType.Opened]: 'Annual Review Feedback Page First Opened',
  [OperationType.Saved]: 'Feedback Updated',
  [OperationType.Submitted]: 'Feedback Submitted',
};

const AuditTrailAnnual = ({ open, setOpen, id }: Props) => {
  const myRef = useRef<any>();
  const [data, setData] = useState<AnnualReviewAuditTrail[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchAudit = async (id: string) => {
    setLoading(true);
    const { error, data } = await getAnnualReviewAuditTrail(id);
    if (error) return myRef.current?.call('error', error?.message);
    setData(data);
    setLoading(false);
  };

  const formattedDateTime = (dateString: string): string => {
    const date = new Date(dateString);
    // Add 5 hours
    date.setHours(date.getHours() + 5);
    // Add 30 minutes
    date.setMinutes(date.getMinutes() + 30);

    return date.toLocaleString('en-US', {
      month: 'long', // numeric, 2-digit, long, short, narrow
      day: '2-digit', // numeric, 2-digit
      year: 'numeric', // numeric, 2-digit
      hour: 'numeric', // numeric, 2-digit
      minute: 'numeric', // numeric, 2-digit
      hour12: true, // Use 12-hour time
    });
  };

  useEffect(() => {
    if (open) fetchAudit(id);
  }, [open]);

  const filterAuditTrail = (data: AnnualReviewAuditTrail[]) => {
    const filteredData = data.filter(
      (item) =>
        OperationType.Opened === item.operationType ||
        OperationType.Saved === item.operationType ||
        OperationType.Submitted === item.operationType
    );

    if (filteredData.length === 0) return [];
    return (
      <Timeline className="mt-[30px]">
        {filteredData.length > 0 &&
          filteredData
            .map((item, idx: number) => {
              if (!item) return null;
              return (
                <Timeline.Item key={idx}>
                  <Message
                    operationType={item.operationType}
                    changedAt={formattedDateTime(item.changedAt)}
                    changedByUser={item.changedByUser.name}
                  />
                </Timeline.Item>
              );
            })
            .reverse()}
      </Timeline>
    );
  };

  return (
    <>
      <Modal
        title="Audit Trail"
        centered
        open={open}
        onCancel={() => setOpen(false)}
        footer={
          <div>
            <Button onClick={() => setOpen(false)}>Close</Button>
          </div>
        }
      >
        {loading ? (
          <Loading />
        ) : !loading && data.length === 0 ? (
          <Empty />
        ) : (
          <div
            className="overflow-auto h-auto"
            style={{ height: 'auto', maxHeight: 'calc(100vh - 300px)' }}
          >
            {filterAuditTrail(data)}
          </div>
        )}
      </Modal>
      <NotificationAlert ref={myRef} />
    </>
  );
};

export default AuditTrailAnnual;

interface MessageProps {
  operationType: string;
  changedAt: string;
  changedByUser: string;
}

const Message = ({ operationType, changedAt, changedByUser }: MessageProps) => {
  return (
    <div>
      {operationType === OperationType.Opened ||
      operationType === OperationType.Submitted ? (
        <>
          <div>
            <b> {OperationTypeLabel[operationType]} </b>by {changedByUser}{' '}
          </div>
          <div className="text-[#6C6C6C] mt-2">
            <div className="text-[12px]">{changedAt}</div>
          </div>
        </>
      ) : (
        <>
          <div>
            <b> {OperationTypeLabel[operationType]} </b>by {changedByUser}{' '}
          </div>
          <div className="text-[#6C6C6C] mt-2">
            <div className="text-[12px]">Updates saved on {changedAt}</div>
          </div>
        </>
      )}
    </div>
  );
};
